import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";

import { fetchNavbarAndFooter } from "lib/fetchers/common";
import { fetchArticles } from "lib/fetchers/articles";
import type { HomepageProps } from "lib/types/homepage";
import type { ArticleListRequestQuery, ArticleShort } from "lib/types/article";
import { fetchHomePageData } from "content-ui/homepage/fetcher";

import { HomepageView } from "content-ui/homepage";
import { getFirstNumberOfUniqueArticles } from "content-ui/homepage/utils/featured";
import type { SectorType } from "content-ui/homepage/utils/featured";
import type { SeoData } from "lib/types/datoBlocks";

import ErrorBoundary from "components/errorBoundary/errorBoundary";
import AdManager from "components/thirdPartyScripts/adManager";
import { adConfigHome } from "components/thirdPartyScripts/adConfig";
import { upcomingTalks } from "content-ui/talks/utils";
import PreviewModeBanner from "components/previewModeBanner/previewModeBanner";
import Navigation from "components/navigation/navigation";
import Footer from "components/footer/footer";
import PaywallContent from "components/paywallContent/paywallContent";
import { fetchCalloutsList } from "lib/fetchers/siteWide";
import {
  filterFreeToReadArticles,
  filterCollaborationArticles,
} from "lib/cms/articleFiltering";
import {
  FEED_TABS,
  DEFAULT_TAB_LABELS,
} from "components/feedsSection/constants";
import type { ArticleFeedSectionData } from "components/feedsSection/types";

/**
 * Extract article IDs from DatoCMS data structure
 */
const extractArticleIds = (
  datoHomepage: any,
  field:
    | "heroArticle"
    | "featuredHeroArticles"
    | "popularArticles"
    | "insightsFeaturedArticles"
): string[] => {
  const ids: string[] = [];

  if (field === "heroArticle" && datoHomepage.heroArticle?.articleId) {
    ids.push(String(datoHomepage.heroArticle.articleId));
  }

  if (
    field === "featuredHeroArticles" &&
    datoHomepage.featuredHeroArticles?.length
  ) {
    datoHomepage.featuredHeroArticles.forEach((item: any) => {
      if (item.articleId) {
        ids.push(String(item.articleId));
      }
    });
  }

  if (
    field === "popularArticles" &&
    datoHomepage.popularArticles?.[0]?.featuredArticle?.length
  ) {
    datoHomepage.popularArticles[0].featuredArticle.forEach((item: any) => {
      if (item.articleId) {
        ids.push(String(item.articleId));
      }
    });
  }

  if (
    field === "insightsFeaturedArticles" &&
    datoHomepage.insightsFeaturedArticles?.[0]?.featuredArticle?.length
  ) {
    datoHomepage.insightsFeaturedArticles[0].featuredArticle.forEach(
      (item: any) => {
        if (item?.articleId) {
          ids.push(String(item.articleId));
        }
      }
    );
  }

  return ids;
};

/**
 * Map fetched articles to their IDs to maintain DatoCMS order
 */
const mapArticlesByIds = (
  fetchedArticles: ArticleShort[] | undefined,
  articleIds: string[]
): ArticleShort[] => {
  if (!fetchedArticles) return [];

  const articleMap = new Map(fetchedArticles.map((a) => [String(a.id), a]));

  return articleIds
    .map((id) => articleMap.get(id))
    .filter((a): a is ArticleShort => a !== undefined);
};

/**
 * Transform feed articles into ArticleFeedSectionData
 */
const transformFeedsSectionData = (
  popularArticles: ArticleShort[],
  allArticles: ArticleShort[],
  freeToReadArticlesSource: ArticleShort[],
  collaborationArticlesSource: ArticleShort[],
  tabNames?: {
    popular?: string;
    freeToRead?: string;
    collaborations?: string;
  },
  tabOrder?: {
    popular?: number;
    freeToRead?: number;
    collaborations?: number;
  },
  ctas?: {
    popular?: { text?: string; link?: string };
    freeToRead?: { text?: string; link?: string };
    collaborations?: { text?: string; link?: string };
  }
): ArticleFeedSectionData => {
  const tabLabels = {
    [FEED_TABS.POPULAR]:
      tabNames?.popular || DEFAULT_TAB_LABELS[FEED_TABS.POPULAR],
    [FEED_TABS.FREE_TO_READ]:
      tabNames?.freeToRead || DEFAULT_TAB_LABELS[FEED_TABS.FREE_TO_READ],
    [FEED_TABS.COLLABORATIONS]:
      tabNames?.collaborations || DEFAULT_TAB_LABELS[FEED_TABS.COLLABORATIONS],
  };

  return {
    popularArticles,
    freeToReadArticles: filterFreeToReadArticles(
      freeToReadArticlesSource
    ).slice(0, 6),
    collaborationArticles: filterCollaborationArticles(
      collaborationArticlesSource
    ).slice(0, 6),
    tabLabels,
    tabOrder: {
      [FEED_TABS.POPULAR]: tabOrder?.popular,
      [FEED_TABS.FREE_TO_READ]: tabOrder?.freeToRead,
      [FEED_TABS.COLLABORATIONS]: tabOrder?.collaborations,
    },
    ctaText: ctas?.popular?.text || "",
    ctaLink: ctas?.popular?.link || "",
    secondTabCta: {
      text: ctas?.freeToRead?.text || "",
      link: ctas?.freeToRead?.link || "",
    },
    thirdTabCta: {
      text: ctas?.collaborations?.text || "",
      link: ctas?.collaborations?.link || "",
    },
  };
};

export async function getServerSideProps({ res, preview = false }) {
  res.setHeader(
    "Cache-Control",
    "public, s-maxage=300, stale-while-revalidate=59"
  );

  const query: ArticleListRequestQuery = {
    page: 1,
    format: "abbreviated",
    placeholderImages: false,
  };

  // Fetch core data in parallel
  const [sitewideOptionsNavbar, calloutListWithVariants, dato] =
    await Promise.all([
      fetchNavbarAndFooter(),
      fetchCalloutsList(),
      fetchHomePageData({ preview }),
    ]);

  // Extract article IDs from DatoCMS for different sections
  const heroArticleIds = extractArticleIds(dato.homepage, "heroArticle");
  const featuredHeroArticleIds = extractArticleIds(
    dato.homepage,
    "featuredHeroArticles"
  );
  const popularArticleIds = extractArticleIds(dato.homepage, "popularArticles");
  const insightsFeaturedArticleIds = extractArticleIds(
    dato.homepage,
    "insightsFeaturedArticles"
  );

  // Fetch articles for hero, featured, popular and insights sections
  const [
    heroArticlesData,
    featuredArticlesData,
    popularArticlesData,
    insightsArticlesData,
  ] = await Promise.all([
    heroArticleIds.length > 0
      ? fetchArticles({ ...query, articles: heroArticleIds.join(",") })
      : Promise.resolve(null),
    featuredHeroArticleIds.length > 0
      ? fetchArticles({
          ...query,
          articles: featuredHeroArticleIds.join(","),
        })
      : Promise.resolve(null),
    popularArticleIds.length > 0
      ? fetchArticles({ ...query, articles: popularArticleIds.join(",") })
      : Promise.resolve(null),
    insightsFeaturedArticleIds.length > 0
      ? fetchArticles({
          ...query,
          articles: insightsFeaturedArticleIds.join(","),
        })
      : Promise.resolve(null),
  ]);

  // Map fetched articles to maintain DatoCMS order
  const heroArticle =
    mapArticlesByIds(heroArticlesData?.data?.articles, heroArticleIds)[0] ||
    null;
  const featuredHeroArticles = mapArticlesByIds(
    featuredArticlesData?.data?.articles,
    featuredHeroArticleIds
  );
  // Sort Popular articles from DatoCMS by id (max 6)
  const popularArticles = mapArticlesByIds(
    popularArticlesData?.data?.articles,
    popularArticleIds
  ).slice(0, 6);
  // Map Insights featured articles
  const insightsFeaturedArticles = mapArticlesByIds(
    insightsArticlesData?.data?.articles,
    insightsFeaturedArticleIds
  );

  // Get featured articles map for sectors
  let sectors: SectorType[] = [];

  // Fetch latest articles, free articles, and collaboration (branded-content) articles in parallel
  const [
    latestArticlesDataPage1,
    latestArticlesDataPage2,
    freeToReadArticlesData,
    collaborationArticlesData,
  ] = await Promise.all([
    fetchArticles({
      ...query,
    }),
    fetchArticles({
      ...query,
      page: 2,
    }),
    fetchArticles({
      format: "abbreviated",
      placeholderImages: false,
      membership_type: "free",
    }),
    fetchArticles({
      format: "abbreviated",
      placeholderImages: false,
      taxonomy: "categories",
      term_slug: "branded-content",
    }),
  ]);

  // Build sectors and deduplication list
  const ignoreArticles = {};

  const latestArticlesAllPages = [
    ...(latestArticlesDataPage1?.data?.articles || []),
    ...(latestArticlesDataPage2?.data?.articles || []),
  ]; // temporary solution until we have an API that supports changing article number per page to reduce overfetching

  const latestArticles = getFirstNumberOfUniqueArticles(
    latestArticlesAllPages,
    10,
    ignoreArticles
  );

  const analysisArticles_used: any[] = [];
  const latestNewsArticles: any[] = [];
  const talks = upcomingTalks(dato.talksPage?.talksData).slice(0, 1);

  // Create FeedsSection data
  const feedsSectionData = transformFeedsSectionData(
    popularArticles,
    latestArticlesAllPages,
    freeToReadArticlesData?.data?.articles || [],
    collaborationArticlesData?.data?.articles || [],
    {
      popular: dato.homepage.popularArticles?.[0]?.tabName,
      freeToRead: dato.homepage.secondTab?.tabName,
      collaborations: dato.homepage.thirdTab?.tabName,
    },
    {
      popular: dato.homepage.popularArticles?.[0]?.tabOrder,
      freeToRead: dato.homepage.secondTab?.tabOrder,
      collaborations: dato.homepage.thirdTab?.tabOrder,
    },
    {
      popular: {
        text: dato.homepage.popularCta?.text,
        link: dato.homepage.popularCta?.url,
      },
      freeToRead: {
        text: dato.homepage.secondTab?.tabCta?.text,
        link: dato.homepage.secondTab?.tabCta?.url,
      },
      collaborations: {
        text: dato.homepage.thirdTab?.tabCta?.text,
        link: dato.homepage.thirdTab?.tabCta?.url,
      },
    }
  );

  return {
    props: {
      __initialState: {
        homepage: {
          sectors,
          latestNews: latestNewsArticles,
          latest: latestArticles,
          analysis: analysisArticles_used,
          talks,
          feedsSectionData,
          dato: {
            homepage: {
              ...dato.homepage,
              heroArticle,
              featuredHeroArticles,
              insightsFeaturedArticles,
            },
          },
        } as HomepageProps,
        sitewideOption: {
          ...dato.sitewideOption,
          ...sitewideOptionsNavbar,
          ...calloutListWithVariants?.calloutList,
        },
      },
      seo: dato.homepage.seo,
      previewMode: preview,
    },
  };
}

type PageProps = {
  seo: SeoData["seo"];
  previewMode: boolean;
};

const Homepage = (props: PageProps) => {
  const title = props.seo!.title || "Homepage";
  const description =
    props.seo!.description ||
    "The latest startup news, analysis and in-depth reporting on Europe's founders, investors and operators. Backed by the Financial Times.";
  const seoImage = props.seo!.image.url || "";

  return (
    <div className="overflow-x-hidden">
      <Head>
        {generateNextSeo({
          title: title,
          description: description,
          canonical: buildCanonicalUrl("/"),
          openGraph: {
            title: title,
            description: description,
            images: [{ url: seoImage }],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <AdManager config={adConfigHome} />

      <Navigation />

      {props.previewMode ? <PreviewModeBanner exitSlug={`/`} /> : null}

      <ErrorBoundary>
        <>
          <HomepageView />
          <PaywallContent
            paywallType={"ar"}
            previewData={undefined}
            preview={props.previewMode}
          />
        </>
      </ErrorBoundary>
      <Footer />
    </div>
  );
};

export default Homepage;
